import { Component } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ModalController, ToastController } from '@ionic/angular';
import { Store, select } from '@ngrx/store';

import { AppState } from '@app/state';
import { selectUploadQueue } from '@app/state/upload-queue/upload-queue.selectors';
import { UploadQueueService } from '@services/upload/upload-queue.service';

/**
 * The sheet behind the upload pill. Presented by `UploadIndicatorComponent`
 * through `ModalController`, which is why it is declared in `UtilsModule`
 * rather than merely referenced.
 *
 * Items reaching `done` are dropped from the slice, so an emptying list is
 * the success case, not a bug -- hence the explicit empty state.
 */
@Component({
  selector: 'app-upload-detail-sheet',
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>Uploads</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="close()">Close</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <ng-container *ngIf="items$ | async as items">
        <ion-list *ngIf="items.length > 0">
          <ion-item *ngFor="let item of items">
            <ion-thumbnail slot="start" *ngIf="item.thumbPath">
              <img [src]="thumb(item.thumbPath)" />
            </ion-thumbnail>
            <ion-label>
              <h3>{{ item.originalFileName }}</h3>
              <p *ngIf="item.state !== 'failed'">
                {{ item.state }} &mdash; {{ percent(item) }}%
              </p>
              <p *ngIf="item.state === 'failed'" class="error">
                {{ item.lastError || 'Upload failed' }}
              </p>
            </ion-label>
            <ion-button
              slot="end"
              fill="clear"
              *ngIf="item.state === 'failed'"
              (click)="retry(item.id)"
              >Retry</ion-button
            >
            <ion-button
              slot="end"
              fill="clear"
              color="danger"
              (click)="cancel(item.id)"
              >Cancel</ion-button
            >
          </ion-item>
        </ion-list>
        <div class="empty" *ngIf="items.length === 0">
          <p>No uploads in progress.</p>
        </div>
      </ng-container>
    </ion-content>
  `,
  styles: [
    `
      .error {
        color: var(--ion-color-danger, #cc0000);
      }
      .empty {
        padding: 24px;
        text-align: center;
        opacity: 0.6;
      }
    `,
  ],
})
export class UploadDetailSheetComponent {
  items$ = this.store$.pipe(select(selectUploadQueue));

  constructor(
    private store$: Store<AppState>,
    private uploads: UploadQueueService,
    private modalCtrl: ModalController,
    private toastCtrl: ToastController
  ) {}

  thumb(path: string) {
    return Capacitor.convertFileSrc(path);
  }

  percent(item: { bytesSent: number; bytesTotal: number }) {
    if (!item.bytesTotal) {
      return 0;
    }
    return Math.round((item.bytesSent / item.bytesTotal) * 100);
  }

  /**
   * `retry` and `cancel` go through the plugin, which rejects on a stale id
   * or on a binary the uploader was never linked into. Left unhandled that is
   * an unhandled promise rejection and, worse, silence: the row does not move
   * and the tap looks ignored. So the failure is reported to the user rather
   * than only to the console.
   */
  async retry(id: string) {
    try {
      await this.uploads.retry(id);
    } catch (err) {
      await this.reportFailure('Could not retry that upload', err);
    }
  }

  async cancel(id: string) {
    try {
      await this.uploads.cancel(id);
    } catch (err) {
      await this.reportFailure('Could not cancel that upload', err);
    }
  }

  private async reportFailure(message: string, err: any) {
    console.log(`${message}: `, err);
    const detail = err && err.message ? err.message : '';
    const toast = await this.toastCtrl.create({
      message: detail ? `${message} -- ${detail}` : message,
      duration: 3000,
      position: 'bottom',
      color: 'danger',
    });
    await toast.present();
  }

  close() {
    void this.modalCtrl.dismiss();
  }
}
